import { useRef, useState } from 'react';
import FotoAssinada from './FotoAssinada';
import type { InscritoPresenca } from '../types';

interface Props {
  linha: InscritoPresenca;
  avaliando: boolean;
  liberando: boolean;
  aoConfirmar: () => void;
  aoRecusar: (motivo: string) => void;
  aoLiberarReenvio: () => void;
  aoAbrirHistorico: () => void;
  aoAmpliar: (url: string) => void;
}

const MOTIVOS_RAPIDOS = [
  'gesto não conferiu',
  'não dá para reconhecer a pessoa',
  'foto fora da formação',
];

function formatarHora(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return min === '00' ? `${hh}h` : `${hh}h${min}`;
}

export default function CardAguardando({
  linha,
  avaliando,
  liberando,
  aoConfirmar,
  aoRecusar,
  aoLiberarReenvio,
  aoAbrirHistorico,
  aoAmpliar,
}: Props) {
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const semFoto = linha.foto_apagada || !linha.foto_url;

  function abrirRecusa() {
    setRecusando(true);
    setMotivo('');
  }

  function escolherMotivo(m: string) {
    setMotivo(m);
  }

  function escolherOutro() {
    setMotivo('');
    inputRef.current?.focus();
  }

  function confirmarRecusa() {
    if (!motivo.trim()) return;
    aoRecusar(motivo.trim());
  }

  const eOutro = recusando && !!motivo && !MOTIVOS_RAPIDOS.includes(motivo);

  const porQuem = linha.registrada_por_nome || '—';
  const quando = formatarHora(linha.registrada_em);

  return (
    <div className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
      <div className="flex gap-3">
        {semFoto ? (
          <div className="flex h-[58px] w-[58px] shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-400">
            <i className="ri-image-line text-xl" />
          </div>
        ) : (
          <FotoAssinada caminho={linha.foto_url as string} aoAmpliar={aoAmpliar} />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-[15px] font-bold text-foreground-950">{linha.nome}</p>
            <button
              type="button"
              onClick={aoAbrirHistorico}
              className="shrink-0 cursor-pointer whitespace-nowrap text-xs font-medium text-primary-600 hover:underline"
            >
              histórico
            </button>
          </div>
          <p className="mt-0.5 text-xs text-secondary-500">
            enviada por {porQuem}
            {quando ? ` · ${quando}` : ''}
          </p>
          {linha.tentativas >= 2 && (
            <p className="mt-1 text-xs text-secondary-400">
              {linha.tentativas} fotos já enviadas
            </p>
          )}
        </div>
      </div>

      {recusando ? (
        <div className="mt-4">
          <p className="text-xs font-medium text-foreground-700">
            Motivo da recusa (obrigatório)
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {MOTIVOS_RAPIDOS.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => escolherMotivo(m)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  motivo === m
                    ? 'bg-red-600 text-white'
                    : 'border border-secondary-300 bg-white text-foreground-700 hover:bg-background-100'
                }`}
              >
                {m}
              </button>
            ))}
            <button
              type="button"
              onClick={escolherOutro}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                eOutro
                  ? 'bg-red-600 text-white'
                  : 'border border-secondary-300 bg-white text-foreground-700 hover:bg-background-100'
              }`}
            >
              outro
            </button>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Escreva o motivo da recusa"
            className="mt-2 w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none placeholder:text-secondary-400 focus:border-primary-400"
          />
          <div className="mt-3 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={confirmarRecusa}
              disabled={avaliando || !motivo.trim()}
              className="h-11 min-h-[44px] flex-1 cursor-pointer whitespace-nowrap rounded-md bg-red-600 px-4 text-sm font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
            >
              {avaliando ? '…' : 'Confirmar recusa'}
            </button>
            <button
              type="button"
              onClick={() => setRecusando(false)}
              className="h-11 min-h-[44px] flex-1 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 text-sm font-bold text-foreground-800 transition-colors hover:bg-background-100"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={aoConfirmar}
              disabled={avaliando}
              className="flex h-11 min-h-[44px] flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-green-600 px-4 text-sm font-bold text-white transition-colors hover:bg-green-700 disabled:opacity-60"
            >
              <i className="ri-check-line" />
              {avaliando ? '…' : 'Confirmar'}
            </button>
            <button
              type="button"
              onClick={abrirRecusa}
              disabled={avaliando}
              className="flex h-11 min-h-[44px] flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-red-600 px-4 text-sm font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
            >
              <i className="ri-close-line" />
              Recusar
            </button>
          </div>

          {linha.tentativas >= 3 && (
            <button
              type="button"
              onClick={aoLiberarReenvio}
              disabled={liberando}
              className="mt-3 h-11 min-h-[44px] w-full cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 text-sm font-bold text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-60"
            >
              {liberando ? '…' : 'Liberar mais uma tentativa'}
            </button>
          )}
        </>
      )}
    </div>
  );
}